import React from "react";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {
    PiArrowDown,
    PiArrowFatLinesDown,
    PiCheckCircle,
    PiShoppingCartSimple,
    PiWarningOctagon,
} from "react-icons/pi";
import {formatQuantity, GROUP_PALETTE, TREND_DOWN_COLOR, TREND_UP_COLOR} from "./format.js";

/**
 * Gemeinsame Bausteine der Lagerstrategie-Unterseite (Annahmen-Panel,
 * Kennzahlen-Kacheln, Handlungslisten, Diagramm, Detailtabelle). Bewusst in
 * einer eigenen Datei, weil mehrere dieser Komponenten dieselbe Einstufung
 * (recommendation), denselben URL-Aufbau für die Rechenannahmen und dieselbe
 * Bestands-je-Lager-Aufschlüsselung brauchen.
 *
 * Die Einstufungsfarben sind bewusst NICHT neu erfunden, sondern greifen auf
 * die bereits in format.js festgelegten, geprüften Farben zurück (siehe
 * Vorgabe "Farbpalette aus format.js unverändert übernehmen"): die drei
 * "zu viel Bestand"-Einstufungen (auslisten/abbauen/reduzieren) teilen sich
 * TREND_DOWN_COLOR und unterscheiden sich stattdessen durch Symbol und Text,
 * "nachbestellen" bekommt die erste, bereits für die ABC-Klasse A feste
 * Palettenfarbe, "ok" TREND_UP_COLOR - wie bei ABC_COLORS ein fester,
 * nicht-zyklischer Slot einer bestehenden Palette, keine neue Farbe.
 */
export const RECOMMENDATION_META = {
    auslisten: {label: "Auslisten prüfen", color: TREND_DOWN_COLOR, Icon: PiWarningOctagon},
    abbauen: {label: "Abbauen", color: TREND_DOWN_COLOR, Icon: PiArrowFatLinesDown},
    reduzieren: {label: "Reduzieren", color: TREND_DOWN_COLOR, Icon: PiArrowDown},
    nachbestellen: {label: "Nachbestellen", color: GROUP_PALETTE[0], Icon: PiShoppingCartSimple},
    ok: {label: "Im Rahmen", color: TREND_UP_COLOR, Icon: PiCheckCircle},
};

export const RECOMMENDATION_ORDER = ["auslisten", "abbauen", "reduzieren", "nachbestellen", "ok"];

/**
 * Badge für die Einstufung - Farbe ist nie der einzige Träger der
 * Information: Symbol und Text stehen immer mit dabei (siehe globale
 * Vorgabe). Optional mit der fachlichen Begründung als Tooltip.
 */
export function RecommendationBadge({value, reason, showReason = true}) {
    const meta = RECOMMENDATION_META[value];
    if (!meta) {
        return <span>–</span>;
    }
    const {label, color, Icon} = meta;
    const badge = (
        <span
            className="badge d-inline-flex align-items-center gap-1"
            style={{backgroundColor: color, color: "#fff"}}
        >
            <Icon aria-hidden="true" />
            {label}
        </span>
    );
    if (!showReason || !reason) {
        return badge;
    }
    return (
        <OverlayTrigger
            placement="top"
            overlay={<Popover><Popover.Body className="small">{reason}</Popover.Body></Popover>}
        >
            {/* trigger bleibt beim Standard (hover + focus) - "click" zusätzlich in
                dieses Array aufzunehmen klingt naheliegend, kollidiert aber mit
                dessen Toggle-Logik: ein echter Tap feuert zuerst Fokus (öffnet)
                und danach Klick (schaltet um, schließt also sofort wieder). Der
                onClick fokussiert die Fläche stattdessen selbst - dasselbe Öffnen
                wie beim Überfahren mit der Maus, nur über einen garantierten
                Fokuswechsel statt auf das teils unzuverlässige Tap-zu-Fokus
                mancher mobiler Browser zu vertrauen. */}
            <span
                tabIndex={0}
                role="button"
                style={{cursor: "help"}}
                onClick={(event) => event.currentTarget.focus()}
            >
                {badge}
            </span>
        </OverlayTrigger>
    );
}

const numberFormatterPallets = new Intl.NumberFormat("de-DE", {minimumFractionDigits: 1, maximumFractionDigits: 1});

/** Paletten immer mit genau einer Nachkommastelle, siehe Vorgabe. */
export function formatPallets(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return `${numberFormatterPallets.format(value)} Pal.`;
}

/**
 * Baut die Basis-URL der Lagerstrategie-Endpunkte mit den sechs in der
 * Annahmen-Leiste änderbaren Rechenannahmen als feste Query-Parameter -
 * strukturgleich zu withQuery() in DetailDataGrid.jsx, damit DataGrid seine
 * eigenen Parameter (Suche/Sortierung/Filter/Seite) unverändert ergänzen
 * kann. "warehouse_ids" und "demand_window_months" bleiben absichtlich beim
 * Server-Standard (SNV Weilheim, 12 Monate) - die Oberfläche bietet dafür
 * keine Bedienelemente an (siehe Auftrag).
 *
 * "level" (Auswertungsebene "article"/"parent", siehe FilterBar.jsx) muss
 * genau wie die sechs Rechenannahmen als fester Query-Parameter mitgehen -
 * sonst würde die Lagerstrategie-Detailtabelle beim Umschalten der
 * Auswertungsebene weiterhin auf Artikel-Ebene rechnen.
 */
export function withAssumptionParams(baseUrl, params) {
    const search = new URLSearchParams();
    if (params) {
        ["lead_time_dosen", "lead_time_marker", "lead_time_default", "service_level", "reorder_cycle_days", "units_per_pallet", "level"].forEach((key) => {
            if (params[key] !== null && params[key] !== undefined) {
                search.set(key, params[key]);
            }
        });
    }
    const query = search.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
}

/**
 * Bestand je Lager als Tooltip-Aufschlüsselung - macht sichtbar, dass
 * standardmäßig nur SNV Weilheim in die Berechnung eingeht und Lahr
 * Hauptlager (Platzhalterdaten) ausgeschlossen bleibt, siehe fachlicher
 * Hinweis im Auftrag. Ein hover-only-Popover ist auf Touch-Geräten
 * unerreichbar - der `onClick` unten fokussiert die Fläche deshalb selbst,
 * was dieselbe (unveränderte) Fokus-Auslösung öffnet wie beim Überfahren mit
 * der Maus. "click" zusätzlich ins `trigger`-Array aufzunehmen läge näher,
 * kollidiert aber mit dessen Toggle-Logik: ein echter Tap feuert zuerst
 * Fokus (öffnet) und danach Klick (schaltet um, schließt also sofort
 * wieder) - beobachtet als Popover, das nach rund 300 ms (Fade-Out-Dauer)
 * von selbst wieder verschwand.
 */
export function StockByWarehousePopover({stockByWarehouse, children}) {
    const rows = stockByWarehouse || [];
    if (rows.length === 0) {
        return children;
    }
    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Popover>
                    <Popover.Header as="h6">Bestand je Lager</Popover.Header>
                    <Popover.Body className="small p-2">
                        <table className="table table-sm mb-1">
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.warehouse_id}>
                                        <td className="pe-3">
                                            {row.warehouse_name || row.warehouse_id}
                                            {!row.included && <span className="text-muted"> (nicht in der Berechnung)</span>}
                                        </td>
                                        <td className="text-end fw-semibold">{formatQuantity(row.quantity)} Stk.</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="text-muted" style={{maxWidth: 260}}>
                            Nur als „in der Berechnung" markierte Lager fließen in den aktuellen Bestand ein - siehe Hinweis zur
                            Bestandsquelle oben auf der Seite.
                        </div>
                    </Popover.Body>
                </Popover>
            }
        >
            <span
                tabIndex={0}
                role="button"
                style={{cursor: "help", borderBottom: "1px dotted currentColor"}}
                onClick={(event) => event.currentTarget.focus()}
            >
                {children}
            </span>
        </OverlayTrigger>
    );
}
